/**
 * postMessage protocol between this app (Weavy iframe) and the Figma parent
 * frame that hosts it. Directions are from Weavy's perspective:
 *
 *   - `outbound` = Weavy → Figma parent
 *   - `inbound`  = Figma parent → Weavy
 *
 * The Figma side maintains the mirror of this contract in
 * `web/js/weave/weave_iframe_view.tsx` (with directions inverted there). When
 * adding or renaming a message type, update both files together.
 *
 * Both sides validate `event.origin` before acting on a message — see
 * `isFigmaEmbedAllowedOrigin` in `auth.consts.ts` for the Weavy-side check.
 */
export const FIGMA_EMBED_MESSAGE_TYPES = {
  outbound: {
    /** Ask Figma for a freshly-minted JWT (response: `TOKEN_REFRESH`). */
    TOKEN_REQUEST: 'WEAVE_TOKEN_REQUEST',
    /**
     * "Back to files" clicked. Figma dispatches its canonical
     * `fullscreenActions.backToFiles` so navigation/history/recents stay
     * consistent with other fullscreen editors. Payload `{ openInNewTab }`:
     * when true (Cmd/Ctrl-click), Figma opens the file browser in a new tab
     * instead of navigating the current one.
     */
    BACK_TO_FILES: 'WEAVE_BACK_TO_FILES',
    /**
     * Top-right "Share" button clicked. Figma opens its canonical
     * `FILE_PERMISSIONS_MODAL` for the open file — file permissions are
     * Figma-owned, so we route through the same modal the regular
     * fullscreen-toolbar Share button uses.
     */
    OPEN_SHARE_MODAL: 'WEAVE_OPEN_SHARE_MODAL',
    /**
     * `File ▶ New Weave` clicked. Figma dispatches its canonical
     * `createNewFile` flow. Only `editorType: 'weave'` is accepted today.
     */
    CREATE_FILE: 'WEAVE_CREATE_FILE',
    /**
     * `File ▶ Make a copy…` clicked. Figma runs `useDuplicateFile` against
     * the currently-open file. No payload — the parent already owns
     * `fileKey`. Plan-picker / paywall modals are Figma-owned.
     */
    DUPLICATE_FILE: 'WEAVE_DUPLICATE_FILE',
    /**
     * Ask Figma for current create/edit permission booleans. Figma answers with
     * `PERMISSIONS_REFRESH` and also pushes the same message proactively when a
     * value flips.
     */
    PERMISSIONS_REQUEST: 'WEAVE_PERMISSIONS_REQUEST',
    /**
     * Ask Figma for the current multiplayer-presence list (other users
     * connected to the same Weave file) so we can render avatars next to
     * the Share button. Figma answers with `PRESENCE_REFRESH` and pushes
     * the same message proactively when the list changes.
     */
    PRESENCE_REQUEST: 'WEAVE_PRESENCE_REQUEST',
    /**
     * Ask Figma for the current filename-dropdown disabled-state. Figma
     * answers with `FILE_MENU_STATE` and pushes the same message
     * proactively when any input flips.
     */
    FILE_MENU_STATE_REQUEST: 'WEAVE_FILE_MENU_STATE_REQUEST',
    /** Ask Figma for the shell environment we're running inside (desktop app vs web browser). */
    SHELL_STATE_REQUEST: 'WEAVE_SHELL_STATE_REQUEST',
    /**
     * Filename-dropdown `Go to project` clicked. Figma opens the file's
     * parent folder URL in a new tab. Fire-and-forget.
     */
    OPEN_FIGMA_PROJECT: 'WEAVE_OPEN_FIGMA_PROJECT',
    /**
     * Filename-dropdown `Move file…` clicked. Figma opens its canonical
     * move-to-project modal for the open file. Fire-and-forget; the modal
     * itself owns the drafts-upgrade billing gate.
     */
    OPEN_FIGMA_MOVE_FILE_MODAL: 'WEAVE_OPEN_FIGMA_MOVE_FILE_MODAL',
    /**
     * Filename-dropdown `Move to trash` clicked. Figma dispatches
     * `tryDeleteFiles`, which removes the file and tears down the iframe.
     */
    MOVE_FIGMA_FILE_TO_TRASH: 'WEAVE_MOVE_FIGMA_FILE_TO_TRASH',
    /**
     * Filename-dropdown `Pin to workspace` clicked. Figma routes to
     * `useTryPinFileToWorkspace` (flash error on max-pins, otherwise
     * `ADD_PIN_MODAL`).
     */
    PIN_FIGMA_FILE_TO_WORKSPACE: 'WEAVE_PIN_FIGMA_FILE_TO_WORKSPACE',
    /**
     * Filename-dropdown `Add to sidebar ▶` — `{ remove, sectionId? }`. Figma
     * routes to `favoriteActions.addFileOrRepoFavorite` / `…removeFileOrRepoFavorite`.
     */
    TOGGLE_FIGMA_SIDEBAR_FAVORITE: 'WEAVE_TOGGLE_FIGMA_SIDEBAR_FAVORITE',
    /**
     * `Help and account ▶ Legal summary` clicked. Figma opens its `/legal`
     * page in a new tab — same href the canonical fullscreen menu's
     * `open-legal-summary` action uses.
     */
    OPEN_LEGAL_SUMMARY: 'WEAVE_OPEN_LEGAL_SUMMARY',
    /**
     * `Help and account ▶ Account settings` clicked. Figma shows its
     * canonical `ACCOUNT_SETTINGS_MODAL` on the Account tab — same path
     * `bindings.tsx#openSettings()` uses.
     */
    OPEN_ACCOUNT_SETTINGS: 'WEAVE_OPEN_ACCOUNT_SETTINGS',
    /**
     * `Help and account ▶ Log out` clicked. Figma dispatches
     * `userActions.logOutAllUsers()` — same handler the canonical
     * fullscreen menu's `sign-out` action uses. Logging out tears down
     * the iframe (top-frame navigation).
     */
    LOG_OUT: 'WEAVE_LOG_OUT',
    /** Ask Figma for the authoritative `fig_file.name`. */
    FILE_NAME_REQUEST: 'WEAVE_FILE_NAME_REQUEST',
    /** User committed a rename in the Weavy editor header. */
    FILE_RENAMED: 'WEAVE_FILE_RENAMED',
    /**
     * `setRecipePoster` in weave client called. Payload: `{ url: string }` — the canonical poster
     * URL the server returned. Figma updates file checkpoint thumbnail URL
     */
    SET_FILE_COVER_PHOTO: 'WEAVE_SET_FILE_COVER_PHOTO',
    /**
     * Ask Figma for the current AI balance menu card view model. Figma owns
     * the credit rules and responds with `AI_BALANCE_REFRESH`.
     */
    AI_BALANCE_REQUEST: 'WEAVE_AI_BALANCE_REQUEST',
    /**
     * Ask Figma whether the current user can run AI right now. Figma owns the
     * credit rules and responds with `AI_METER_REFRESH`.
     */
    AI_METER_REQUEST: 'WEAVE_AI_METER_REQUEST',
    /**
     * `AI balance ▶ Manage credits` clicked. Figma runs the same callback as
     * its canonical AI balance menu card.
     */
    AI_BALANCE_MANAGE_CREDITS: 'WEAVE_AI_BALANCE_MANAGE_CREDITS',
    /**
     * `AI balance ▶ Request credits` clicked. Figma runs the same callback as
     * its canonical AI balance menu card.
     */
    AI_BALANCE_REQUEST_CREDITS: 'WEAVE_AI_BALANCE_REQUEST_CREDITS',
    /**
     * `AI balance ▶ Request more` clicked for user plan-credit limit exhaustion.
     * Figma opens the AI credit requests modal (same as `AiCreditRequestsButton`).
     */
    AI_BALANCE_USER_LIMIT_REQUEST_CREDITS: 'WEAVE_AI_BALANCE_USER_LIMIT_REQUEST_CREDITS',
    /**
     * A Figma-driven toolbar button (see `VIEWER_TOOLBAR_STATE`) was clicked.
     * Parent routes by `buttonId` (e.g. `ask_to_edit`, `make_a_copy`).
     */
    TOOLBAR_BUTTON_CLICKED: 'WEAVE_TOOLBAR_BUTTON_CLICKED',
    /**
     * Bounding rect of a toolbar button (iframe client coordinates) so the
     * parent can anchor shell overlays. Sent in response to `REQUEST_BUTTON_RECT`.
     */
    BUTTON_RECT: 'WEAVE_BUTTON_RECT',
    /**
     * Ask Figma for the current viewer toolbar state. Figma answers with
     * `VIEWER_TOOLBAR_STATE`. Sent on mount by `FigmaViewerButtons` so the
     * initial state is delivered even if the proactive push arrived before the
     * listener was registered.
     */
    VIEWER_TOOLBAR_STATE_REQUEST: 'WEAVE_VIEWER_TOOLBAR_STATE_REQUEST',
    /**
     * Throttled signal that the user is interacting *inside* the iframe
     * (pointer/key/wheel). The parent's edit-lock idle timer only observes Figma
     * chrome events; without this an editor working entirely inside Weave would
     * look idle and could lose the lock mid-edit. Fire-and-forget.
     */
    USER_ACTIVITY: 'WEAVE_USER_ACTIVITY',
    /**
     * Ask Figma for the current edit-lock role. Sent on mount (and so on every
     * reload), mirroring the other *_REQUEST handshakes. Figma answers with
     * `ROLE_CHANGE`. Without this a freshly-reloaded iframe would sit on the JWT
     * null-fallback until the next proactive push, briefly treating a locked-out
     * CAN_EDIT user as an editor.
     */
    ROLE_REQUEST: 'WEAVE_ROLE_REQUEST',
    /**
     * Logged-out viewer clicked the anonymous presence avatar. Figma opens its
     * canonical sign-up/login modal (the same one the regular fullscreen
     * multiplayer avatar uses). Fire-and-forget.
     */
    REQUEST_AUTH_MODAL: 'WEAVE_REQUEST_AUTH_MODAL',
    /**
     * Whether Weavy is currently rendering the top-right viewer toolbar (the
     * Share button + presence). Figma uses this to only show the logged-out
     * sign-up banner alongside that toolbar — never while the file is loading or
     * errored (when the toolbar is unmounted). Carries `{ visible: boolean }`.
     */
    VIEWER_TOOLBAR_VISIBLE: 'WEAVE_VIEWER_TOOLBAR_VISIBLE',
    /**
     * Editor canvas painted its first frame. Sent once per (re)boot, after React
     * Flow init + a couple of frames. Figma holds its loading overlay + chrome
     * until this lands so nothing flashes over a black canvas (unlike
     * `VIEWER_TOOLBAR_STATE_REQUEST`, which fires at JS mount, not paint).
     */
    CANVAS_READY: 'WEAVE_CANVAS_READY',
    /**
     * Weavy is about to `window.location.reload()` in place (takeover/demotion).
     * Sent just before so the parent re-raises its overlay and hides chrome
     * before the canvas blanks. Fire-and-forget.
     */
    RELOADING: 'WEAVE_RELOADING',
  },
  inbound: {
    /** Figma's response to `TOKEN_REQUEST`, carrying the JWT. */
    TOKEN_REFRESH: 'WEAVE_TOKEN_REFRESH',
    /** Figma's response to `PERMISSIONS_REQUEST` and proactive update on change. */
    PERMISSIONS_REFRESH: 'WEAVE_PERMISSIONS_REFRESH',
    /** Figma's response to `FILE_NAME_REQUEST` and proactive latest-name update. */
    FILE_NAME: 'WEAVE_FILE_NAME',
    /** Figma's response to `PRESENCE_REQUEST` and proactive update on change. */
    PRESENCE_REFRESH: 'WEAVE_PRESENCE_REFRESH',
    /**
     * Filename-dropdown disabled/visibility state mirroring canonical
     * `FilenameViewDropdown`. Push-only on subscribe + on change.
     */
    FILE_MENU_STATE: 'WEAVE_FILE_MENU_STATE',
    /** Figma's response to `AI_BALANCE_REQUEST`, carrying the rendered view model. */
    AI_BALANCE_REFRESH: 'WEAVE_AI_BALANCE_REFRESH',
    /** Figma's response to `AI_METER_REQUEST`, carrying `{ hasCredits }`. */
    AI_METER_REFRESH: 'WEAVE_AI_METER_REFRESH',
    /**
     * Figma's response to `SHELL_STATE_REQUEST`. Carries `{ shellType: WeaveShellType }` —
     * an enum string (`'desktop'` | `'web'`) so we can grow it without a wire break.
     */
    SHELL_STATE: 'WEAVE_SHELL_STATE',
    /**
     * Figma pushes the set of extra viewer toolbar buttons to render inside
     * the embed `ActionsBar` `children` slot. Empty `buttons` clears them.
     */
    VIEWER_TOOLBAR_STATE: 'WEAVE_VIEWER_TOOLBAR_STATE',
    /**
     * Ask Weavy to report `BUTTON_RECT` for a given `buttonId` after the
     * toolbar has painted.
     */
    REQUEST_BUTTON_RECT: 'WEAVE_REQUEST_BUTTON_RECT',
    /**
     * Figma pushes the number of pending edit-role requests for the open file.
     * Weavy renders a badge dot on `FigmaEmbedShareButton` when > 0.
     */
    SHARE_BADGE_REFRESH: 'WEAVE_SHARE_BADGE_REFRESH',
    /**
     * Figma pushes the edit-lock role for the current user: `'editor'` (holds the
     * lock) or `'viewer'` (locked out / read-only). Weavy maps this onto its
     * workflow role so a non-owner can't edit or save. Authoritative over the JWT
     * `permission`, which only sets the ceiling (a CAN_VIEW user stays read-only
     * regardless). Also carries `reload: boolean` — true when promoting to editor
     * after another user held the lock, signalling Weavy to reload so it picks up
     * their saves (false on a self-reacquire after idle).
     */
    ROLE_CHANGE: 'WEAVE_ROLE_CHANGE',
    /**
     * Figma's desktop native Edit-menu `Undo` was invoked while this Weave file
     * is the active editor. The OS owns the `Cmd+Z` accelerator so the keystroke
     * never reaches this iframe; Figma forwards it here and we run our own
     * history undo.
     */
    UNDO: 'WEAVE_UNDO',
    /**
     * Figma's desktop native Edit-menu `Redo` counterpart to `UNDO`
     */
    REDO: 'WEAVE_REDO',
    /**
     * Figma's desktop native Edit-menu `Select All` (`Cmd+A` / `Ctrl+A`) was
     * invoked while this Weave file is the active editor.
     */
    SELECT_ALL: 'WEAVE_SELECT_ALL',
    /**
     * Figma's desktop native `Actions` (`Cmd+K`) menu item was invoked while
     * this Weave file is the active editor.
     */
    TOGGLE_ACTIONS: 'WEAVE_TOGGLE_ACTIONS',
    /**
     * Figma's desktop native `Help ▶ Keyboard shortcuts` OS-menu item was invoked
     * while this Weave file is the active editor. Figma forwards it here so we
     * toggle Weavy's own keyboard-shortcuts drawer. Handled in
     * `useFigmaEmbedMenuActions`.
     */
    TOGGLE_KEYBOARD_SHORTCUTS: 'WEAVE_TOGGLE_KEYBOARD_SHORTCUTS',
    /**
     * Figma's in-app Help widget "Keyboard shortcuts" option was invoked while
     * this Weave file is the active editor. Unlike the OS-menu item above, this
     * only ever opens the drawer (never closes it). Handled in
     * `FigmaEmbedModeContext`.
     */
    OPEN_SHORTCUTS_PANEL: 'WEAVE_OPEN_SHORTCUTS_PANEL',
    /**
     * Figma's desktop native Edit-menu `Copy` (`Cmd+C`) was invoked while this
     * Weave file is the active editor.
     */
    COPY: 'WEAVE_COPY',
    /**
     * Figma's desktop native `View ▶ Zoom in / Zoom out / Zoom to 100% / Zoom to
     * fit / Zoom to selection` menu items were invoked while this Weave file is the
     * active editor. Figma forwards them here so we zoom our own canvas, mirroring
     * the in-app zoom shortcuts.
     */
    ZOOM_IN: 'WEAVE_ZOOM_IN',
    ZOOM_OUT: 'WEAVE_ZOOM_OUT',
    ZOOM_RESET: 'WEAVE_ZOOM_RESET',
    ZOOM_TO_FIT: 'WEAVE_ZOOM_TO_FIT',
    ZOOM_TO_SELECTION: 'WEAVE_ZOOM_TO_SELECTION',
  },
} as const;

/**
 * sessionStorage key set just before the demotion (editor → viewer) reload so
 * the post-reload boot defaults the workflow role to read-only until the first
 * `ROLE_CHANGE` arrives — instead of falling back to the JWT `permission` (which
 * would briefly re-enable editor UI like Rename for a now-locked-out CAN_EDIT
 * user). One-shot and tab-scoped: cleared on the next boot so an unrelated manual
 * refresh keeps the normal JWT fallback. Safe because the demotion reload only
 * fires from a Figma shell that speaks the role protocol, so a `ROLE_CHANGE`
 * reply is guaranteed.
 */
export const WEAVE_EMBED_BOOT_AS_VIEWER_KEY = 'weave-embed-boot-as-viewer';
